import logger from '@ayocore/platform/common/helpers/logger';

/**
 * WebPushController - Client-side controller for Web Push subscriptions
 *
 * Handles browser push subscription management, permission requests,
 * and communication with the server for subscription persistence.
 */
export class WebPushController {
  private static instance: WebPushController | null = null;

  private constructor() {
    // VAPID public key will be fetched from server when needed
  }

  static getInstance(): WebPushController {
    if (!WebPushController.instance) {
      WebPushController.instance = new WebPushController();
    }
    return WebPushController.instance;
  }

  /**
   * Check if push notifications are supported in this browser
   */
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  /**
   * Get current notification permission status
   */
  getPermission(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      logger.warn('⚠️ [PushManager] Push notifications not supported');
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      logger.info(`🔔 [PushManager] Permission request result: ${permission}`);
      return permission;
    } catch (error) {
      logger.error('❌ [PushManager] Failed to request permission:', error);
      return 'denied';
    }
  }

  /**
   * Get current push subscription
   */
  async getSubscription(): Promise<PushSubscription | null> {
    if (!this.isSupported()) {
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return subscription;
    } catch (error) {
      logger.error('❌ [PushManager] Failed to get subscription:', error);
      return null;
    }
  }

  /**
   * Create a new push subscription
   */
  async subscribe(vapidPublicKey: string): Promise<PushSubscription | null> {
    if (!this.isSupported()) {
      logger.warn('⚠️ [PushManager] Push notifications not supported');
      return null;
    }

    const permission = this.getPermission();
    if (permission !== 'granted') {
      logger.warn(`⚠️ [PushManager] Permission not granted: ${permission}`);
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;

      // Convert VAPID key to Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });

      logger.info('✅ [PushManager] Push subscription created successfully');
      return subscription;
    } catch (error) {
      logger.error('❌ [PushManager] Failed to create subscription:', error);
      return null;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      const subscription = await this.getSubscription();
      if (subscription) {
        const result = await subscription.unsubscribe();
        logger.info('✅ [PushManager] Push subscription removed');
        return result;
      }
      return true; // Already unsubscribed
    } catch (error) {
      logger.error('❌ [PushManager] Failed to unsubscribe:', error);
      return false;
    }
  }

  /**
   * Save subscription to server via RPC
   */
  async saveSubscription(subscription: PushSubscription): Promise<boolean> {
    try {
      // Import RPC client dynamically to avoid circular dependencies
      const { usePushSubscriptionRepository } = await import('../rpc');
      const pushRepo = usePushSubscriptionRepository();

      const p256dhKey = subscription.getKey('p256dh');
      const authKey = subscription.getKey('auth');

      if (!p256dhKey || !authKey) {
        logger.error('❌ [PushManager] Failed to get subscription keys');
        return false;
      }

      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys_p256dh: this.arrayBufferToBase64(p256dhKey),
        keys_auth: this.arrayBufferToBase64(authKey),
        origin: this.getOrigin(),
        user_agent: navigator.userAgent,
        is_active: true,
      };

      // Check if subscription already exists
      const existing = await pushRepo.findByEndpoint(subscription.endpoint);

      if (existing) {
        // Update existing subscription
        await pushRepo.update(existing.id, {
          ...subscriptionData,
          is_active: true,
          error_count: 0,
          last_error_code: null,
        });
        logger.info('🔄 [PushManager] Updated existing subscription');
      } else {
        // Create new subscription using custom method with proper typing
        await pushRepo.createSubscription(subscriptionData);
        logger.info('✅ [PushManager] Created new subscription');
      }

      return true;
    } catch (error) {
      logger.error('❌ [PushManager] Failed to save subscription:', error);
      return false;
    }
  }

  /**
   * Remove subscription from server
   */
  async removeSubscription(endpoint: string): Promise<boolean> {
    try {
      const { usePushSubscriptionRepository } = await import('../rpc');
      const pushRepo = usePushSubscriptionRepository();

      const existing = await pushRepo.findByEndpoint(endpoint);
      if (existing) {
        await pushRepo.markInactive(existing.id);
        logger.info('🗑️ [PushManager] Marked subscription as inactive');
      }

      return true;
    } catch (error) {
      logger.error('❌ [PushManager] Failed to remove subscription:', error);
      return false;
    }
  }

  /**
   * Enable push notifications (request permission + subscribe + save)
   */
  async enablePush(vapidPublicKey: string): Promise<boolean> {
    try {
      // Step 1: Request permission
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        logger.warn('⚠️ [PushManager] Permission denied by user');
        return false;
      }

      // Step 2: Create subscription
      const subscription = await this.subscribe(vapidPublicKey);
      if (!subscription) {
        logger.error('❌ [PushManager] Failed to create subscription');
        return false;
      }

      // Step 3: Save to server
      const saved = await this.saveSubscription(subscription);
      if (!saved) {
        logger.error('❌ [PushManager] Failed to save subscription');
        return false;
      }

      logger.info('✅ [PushManager] Push notifications enabled successfully');
      return true;
    } catch (error) {
      logger.error('❌ [PushManager] Failed to enable push:', error);
      return false;
    }
  }

  /**
   * Disable push notifications (unsubscribe + remove from server)
   */
  async disablePush(): Promise<boolean> {
    try {
      // Step 1: Get current subscription
      const subscription = await this.getSubscription();

      if (subscription) {
        // Step 2: Remove from server
        await this.removeSubscription(subscription.endpoint);

        // Step 3: Unsubscribe from browser
        await this.unsubscribe();
      }

      logger.info('✅ [PushManager] Push notifications disabled successfully');
      return true;
    } catch (error) {
      logger.error('❌ [PushManager] Failed to disable push:', error);
      return false;
    }
  }

  /**
   * Send test push notification via RPC
   */
  async sendTestPush(): Promise<boolean> {
    try {
      const { sendTestPush: rpcSendTestPush } = await import('../rpc');
      const success = await rpcSendTestPush();
      return !!success;
    } catch (error) {
      logger.error('❌ [WebPushController] Failed to send test push:', error);
      return false;
    }
  }

  /**
   * Get current origin (subdomain)
   */
  private getOrigin(): string {
    return window.location.origin;
  }

  /**
   * Convert VAPID key from base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}
